/**
 * Created by katarinababic on 11.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { ActivitySchedule, ActivityScheduleApi } from '../../Model/Coach/Schedule/ActivitySchedule';
import dayjs from 'dayjs';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styled from '@emotion/styled';
import { Theme } from '../../Theme/Theme';
import { ScheduleEntryRenderer } from './ScheduleEntryRenderer';
import { useTrans } from '../../Store/System/LocalizationStore';

const PageContainer = styled.div`
  min-height: calc(100vh - 64px);
  background: linear-gradient(180deg, ${Theme.Colors.primary}08 0%, ${Theme.Colors.background} 300px);
  padding: 32px 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 32px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    max-width: 700px;
  }
`;

const CalendarCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 24px;
  position: sticky;
  top: 96px;

  @media (max-width: 1024px) {
    position: static;
  }
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  border: none;
  font-family: 'Montserrat', sans-serif;

  .react-calendar__navigation {
    margin-bottom: 16px;

    button {
      font-size: 16px;
      font-weight: 600;
      color: ${Theme.Colors.textColor};
      background: none;
      border: none;
      padding: 8px 12px;
      border-radius: 8px;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        background: ${Theme.Colors.lightestGrey};
      }

      &:disabled {
        opacity: 0.3;
      }
    }
  }

  .react-calendar__month-view__weekdays {
    margin-bottom: 8px;

    .react-calendar__month-view__weekdays__weekday {
      padding: 8px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      color: ${Theme.Colors.warmGrey};

      abbr {
        text-decoration: none;
        border: none;
      }
    }
  }

  .react-calendar__tile {
    position: relative;
    padding: 12px 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${Theme.Colors.textColor};
    background: none;
    border: none;
    border-radius: 12px;
    transition: all 0.2s ease;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: ${Theme.Colors.lightestGrey};
    }

    &.react-calendar__tile--now {
      background: ${Theme.Colors.primary}15;
      font-weight: 700;
    }

    &.react-calendar__tile--active {
      background: ${Theme.Colors.primary};
      color: white;
      font-weight: 600;

      &:hover {
        background: ${Theme.Colors.primary};
      }
    }

    &.marked::after {
      content: '';
      position: absolute;
      bottom: 6px;
      left: 50%;
      transform: translateX(-50%);
      width: 6px;
      height: 6px;
      background: ${Theme.Colors.primary};
      border-radius: 50%;
    }

    &.react-calendar__tile--active.marked::after {
      background: white;
    }

    &.react-calendar__month-view__days__day--neighboringMonth {
      color: ${Theme.Colors.lighterGrey};
    }
  }

  .react-calendar__month-view__days__day--weekend {
    color: ${Theme.Colors.textColor};
  }
`;

const SchedulePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const DayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const DayTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0;
`;

const DaySubtitle = styled.span`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
`;

const EntriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmptyState = styled.div`
  background: ${Theme.Colors.white};
  border-radius: 14px;
  padding: 48px 24px;
  text-align: center;
  border: 1px solid ${Theme.Colors.lighterGrey};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
`;

const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 40px;
  background: linear-gradient(135deg, ${Theme.Colors.primary}15 0%, ${Theme.Colors.primary}08 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
`;

const EmptyIcon = styled.i`
  font-size: 36px;
  color: ${Theme.Colors.primary};
`;

const EmptyTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${Theme.Colors.grey};
  margin: 0;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
`;

export type ScheduleCalendarScreenProps = {};

export const ScheduleCalendarScreen: React.FC<ScheduleCalendarScreenProps> = observer((props) => {
  const { t } = useTrans();

  const [monthSchedule, setMonthSchedule] = useState<ActivitySchedule | undefined>();
  const [schedule, setSchedule] = useState<ActivitySchedule | undefined>();
  const [selectedDay, setSelectedDay] = useState(dayjs().format('YYYY-MM-DD'));
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const selectedDate = dayjs(selectedDay, 'YYYY-MM-DD');
  const isToday = selectedDate.isSame(dayjs(), 'day');

  // Fetch schedule entries for the entire month to mark days
  useEffect(() => {
    const start = currentMonth.startOf('month').format('YYYY-MM-DD');
    const end = currentMonth.endOf('month').format('YYYY-MM-DD');
    ActivityScheduleApi.find({ startDateTime: start, endDateTime: end }).then((res) => setMonthSchedule(res));
  }, [currentMonth]);

  // Fetch schedule entries (scheduled workouts and completed logs) for the selected day
  useEffect(() => {
    const day = selectedDay;
    ActivityScheduleApi.find({ startDateTime: day, endDateTime: day }).then((res) => setSchedule(res));
  }, [selectedDay]);

  // Refresh calendar and list when schedule changes (e.g., after deletion)
  useEffect(() => {
    const handleScheduleChange = () => {
      // re-fetch month schedule to update marked days
      const startMonth = currentMonth.startOf('month').format('YYYY-MM-DD');
      const endMonth = currentMonth.endOf('month').format('YYYY-MM-DD');
      ActivityScheduleApi.find({ startDateTime: startMonth, endDateTime: endMonth })
        .then((res) => setMonthSchedule(res))
        .catch(() => {
          console.log('Failed to fetch month schedule');
        });
      // re-fetch selected day schedule to update list
      const day = selectedDay;
      ActivityScheduleApi.find({ startDateTime: day, endDateTime: day })
        .then((res) => setSchedule(res))
        .catch(() => {
          console.log('Failed to fetch schedule for selected day');
        });
    };
    window.addEventListener('scheduleChanged', handleScheduleChange);
    return () => {
      window.removeEventListener('scheduleChanged', handleScheduleChange);
    };
  }, [currentMonth, selectedDay]);

  const handleDateChange = useCallback((value: any) => {
    let dateVal: Date | null = null;
    if (Array.isArray(value) && value.length > 0) {
      dateVal = value[0];
    } else if (value instanceof Date) {
      dateVal = value;
    }
    if (dateVal) {
      setSelectedDay(dayjs(dateVal).format('YYYY-MM-DD'));
    }
  }, []);

  const handleMonthChange = useCallback(({ activeStartDate }: { activeStartDate: Date | null }) => {
    if (activeStartDate) {
      setCurrentMonth(dayjs(activeStartDate));
    }
  }, []);

  const getTileClassName = useCallback(
    ({ date, view }: { date: Date; view: string }) => {
      if (view !== 'month') return '';
      const ds = dayjs(date).format('YYYY-MM-DD');
      return monthSchedule?.days[ds]?.entries.length ? 'marked' : '';
    },
    [monthSchedule],
  );

  // Prepare list data sorted by start time
  const listData = useMemo(
    () =>
      Object.values(schedule?.days || {})
        .flatMap((day) => day.entries)
        .sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime))),
    [schedule],
  );

  const dayDisplayTitle = isToday
    ? t('scheduleCalendar.today', { defaultValue: 'Today' })
    : selectedDate.format('dddd');

  return (
    <PageContainer>
      <ContentWrapper>
        <SchedulePanel>
          <PageHeader>
            <PageTitle>{t('scheduleCalendar.title', { defaultValue: 'My Schedule' })}</PageTitle>
            <PageSubtitle>
              {t('scheduleCalendar.subtitle', { defaultValue: 'View and manage your planned activities' })}
            </PageSubtitle>
          </PageHeader>

          <DayHeader>
            <div>
              <DayTitle>{dayDisplayTitle}</DayTitle>
              <DaySubtitle>{selectedDate.format('MMMM D, YYYY')}</DaySubtitle>
            </div>
            <DaySubtitle>
              {listData.length}{' '}
              {listData.length === 1
                ? t('scheduleCalendar.activity', { defaultValue: 'activity' })
                : t('scheduleCalendar.activities', { defaultValue: 'activities' })}
            </DaySubtitle>
          </DayHeader>

          {listData.length === 0 ? (
            <EmptyState>
              <EmptyIconWrapper>
                <EmptyIcon className="mdi mdi-calendar-blank-outline" />
              </EmptyIconWrapper>
              <EmptyTitle>{t('scheduleCalendar.emptyTitle', { defaultValue: 'No activities scheduled' })}</EmptyTitle>
              <EmptyDescription>
                {t('scheduleCalendar.emptyDescription', {
                  defaultValue: 'Browse content and schedule activities to see them here',
                })}
              </EmptyDescription>
            </EmptyState>
          ) : (
            <EntriesContainer>
              {listData.map((entry) => (
                <ScheduleEntryRenderer key={entry.id} entry={entry} date={selectedDay} />
              ))}
            </EntriesContainer>
          )}
        </SchedulePanel>

        <CalendarCard>
          <StyledCalendar
            onChange={handleDateChange}
            value={selectedDate.toDate()}
            onActiveStartDateChange={handleMonthChange}
            tileClassName={getTileClassName}
          />
        </CalendarCard>
      </ContentWrapper>
    </PageContainer>
  );
});
